import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Paper, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { MaintenanceService } from '../../services/maintenance.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { PERMISSIONS } from '../../constants/permissions';
import SearchableSelect from '../common/SearchableSelect';
import {
    EquipmentDTO, TechnicianDTO, WORK_ORDER_PRIORITIES, WORK_ORDER_STATUSES, WORK_ORDER_TYPES, WorkOrderDTO,
    WorkOrderPriority, WorkOrderType, mntLabel,
} from '../../interfaces/maintenance';
import { WO_PRIORITY_COLOR, WO_STATUS_COLOR, cell, fmtDateTime } from './woCommon';

/** Work orders. Managers and viewers see all; a technician holding only the work key sees their own. */
const WorkOrderList: React.FC = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const { hasPermission } = useAuth();
    const canManage = hasPermission(PERMISSIONS.MAINTENANCE_WO_MANAGE);
    const seesAll = hasPermission(PERMISSIONS.MAINTENANCE_WO_VIEW_ALL) || !hasPermission(PERMISSIONS.MAINTENANCE_WO_WORK);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [rows, setRows] = useState<WorkOrderDTO[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState('');
    const [type, setType] = useState('');
    const [assignedTo, setAssignedTo] = useState('');
    const [archived, setArchived] = useState(false);
    const [technicians, setTechnicians] = useState<TechnicianDTO[]>([]);

    const [creating, setCreating] = useState(false);
    const [cType, setCType] = useState<WorkOrderType>('CORRECTIVE');
    const [cPriority, setCPriority] = useState<WorkOrderPriority>('NORMAL');
    const [cTitle, setCTitle] = useState('');
    const [cDescription, setCDescription] = useState('');
    const [cEquipmentId, setCEquipmentId] = useState('');
    const [cAssignTo, setCAssignTo] = useState('');
    const [equipment, setEquipment] = useState<EquipmentDTO[]>([]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (seesAll) {
            MaintenanceService.listTechnicians(institutionId).then(setTechnicians).catch(() => setTechnicians([]));
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = seesAll
                ? await MaintenanceService.listWorkOrders(institutionId, { status, type, assignee: assignedTo, archived }, page, size)
                : await MaintenanceService.listMyWorkOrders(institutionId, status, page, size);
            setRows(res.content);
            setTotal(res.totalElements);
        } catch (e) {
            toast.showApiError(e, 'Failed to load work orders');
        } finally {
            setLoading(false);
        }
    }, [institutionId, seesAll, status, type, assignedTo, archived, page, size]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        setCType('CORRECTIVE'); setCPriority('NORMAL'); setCTitle(''); setCDescription(''); setCEquipmentId(''); setCAssignTo('');
        setCreating(true);
        MaintenanceService.listEquipment(institutionId, {}, 0, 100).then(r => setEquipment(r.content)).catch(() => setEquipment([]));
    };

    const searchEquipment = (text: string) => {
        MaintenanceService.listEquipment(institutionId, { q: text }, 0, 50).then(r => setEquipment(r.content)).catch(() => undefined);
    };

    const create = async () => {
        try {
            setSaving(true);
            const wo = await MaintenanceService.createWorkOrder({
                institutionId, type: cType, priority: cPriority, title: cTitle.trim(),
                description: cDescription.trim() || undefined,
                equipmentId: cEquipmentId || undefined, assignedTo: cAssignTo || undefined,
            });
            toast.showSuccess(`Work order ${wo.woNo} created`);
            setCreating(false);
            navigate(`/maintenance/work-orders/${wo.id}`);
        } catch (e) {
            toast.showApiError(e, 'Failed to create the work order');
        } finally {
            setSaving(false);
        }
    };

    const resetPage = () => setPage(0);

    return (
        <Box sx={{ p: 2 }} data-help-page="maint.workOrders.page">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h5">{seesAll ? 'Work orders' : 'My work orders'}</Typography>
                {canManage && (
                    <Button data-help="maint.workOrders.new" variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New work order</Button>
                )}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="maint.workOrders.filter">
                <TextField
                    select size="small" label="Status" value={status} sx={cell(150)} data-help="maint.workOrders.filter.status"
                    onChange={e => { setStatus(e.target.value); resetPage(); }}
                >
                    <MenuItem value="">All</MenuItem>
                    {WORK_ORDER_STATUSES.map(s => <MenuItem key={s} value={s}>{mntLabel(s)}</MenuItem>)}
                </TextField>
                {seesAll && (
                    <>
                        <TextField
                            select size="small" label="Type" value={type} sx={cell(150)} data-help="maint.workOrders.filter.type"
                            onChange={e => { setType(e.target.value); resetPage(); }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {WORK_ORDER_TYPES.map(t => <MenuItem key={t} value={t}>{mntLabel(t)}</MenuItem>)}
                        </TextField>
                        <Box sx={cell(200)}>
                            <SearchableSelect
                                options={technicians} value={assignedTo} size="small" label="Technician" data-help="maint.workOrders.filter.technician"
                                getOptionId={t => t.userId} getOptionLabel={t => t.name}
                                onChange={id => { setAssignedTo(id); resetPage(); }}
                            />
                        </Box>
                        <Box sx={cell(170)}>
                            <FormControlLabel
                                data-help="maint.workOrders.showArchived"
                                label="Show archived"
                                control={<Switch checked={archived} onChange={e => { setArchived(e.target.checked); resetPage(); }} />}
                            />
                        </Box>
                    </>
                )}
            </Box>

            <Paper data-help-section="maint.workOrders.table">
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="maint.workOrders.col.no">No.</TableCell>
                                    <TableCell data-help="maint.workOrders.col.title">Title</TableCell>
                                    <TableCell data-help="maint.workOrders.col.machine">Machine</TableCell>
                                    <TableCell data-help="maint.workOrders.col.type">Type</TableCell>
                                    <TableCell data-help="maint.workOrders.col.priority">Priority</TableCell>
                                    <TableCell data-help="maint.workOrders.col.tech">Technician</TableCell>
                                    <TableCell data-help="maint.workOrders.col.created">Created</TableCell>
                                    <TableCell data-help="maint.workOrders.col.status">Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 && (
                                    <TableRow><TableCell colSpan={8} align="center">No work orders found</TableCell></TableRow>
                                )}
                                {rows.map(r => (
                                    <TableRow
                                        key={r.id} hover sx={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/maintenance/work-orders/${r.id}`)}
                                    >
                                        <TableCell>{r.woNo}</TableCell>
                                        <TableCell>
                                            {r.title}
                                            {r.archivedAt && <Chip size="small" variant="outlined" label="Archived" sx={{ ml: 1 }} />}
                                            {r.machineDown && <Chip size="small" color="error" label="Down" sx={{ ml: 1 }} />}
                                        </TableCell>
                                        <TableCell>{r.equipmentCode ? `${r.equipmentCode} ${r.equipmentName ?? ''}` : r.locationName ?? '-'}</TableCell>
                                        <TableCell>{mntLabel(r.type)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={mntLabel(r.priority)} color={WO_PRIORITY_COLOR[r.priority]} />
                                        </TableCell>
                                        <TableCell>{r.assignedToName ?? '-'}</TableCell>
                                        <TableCell>{fmtDateTime(r.createdAt)}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={mntLabel(r.status)} color={WO_STATUS_COLOR[r.status]} />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
                <TablePagination
                    component="div" count={total} page={page} rowsPerPage={size}
                    rowsPerPageOptions={[10, 20, 50]}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={e => { setSize(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'maint.workOrderCreate.page' } as any}>
                <DialogTitle>New work order</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <TextField size="small" label="Title" value={cTitle} required data-help="maint.workOrderCreate.field.title" onChange={e => setCTitle(e.target.value)} />
                        <TextField select size="small" label="Type" value={cType} data-help="maint.workOrderCreate.field.type" onChange={e => setCType(e.target.value as WorkOrderType)}>
                            {WORK_ORDER_TYPES.map(t => <MenuItem key={t} value={t}>{mntLabel(t)}</MenuItem>)}
                        </TextField>
                        <TextField
                            select size="small" label="Priority" value={cPriority} data-help="maint.workOrderCreate.field.priority"
                            onChange={e => setCPriority(e.target.value as WorkOrderPriority)}
                        >
                            {WORK_ORDER_PRIORITIES.map(p => <MenuItem key={p} value={p}>{mntLabel(p)}</MenuItem>)}
                        </TextField>
                        <SearchableSelect
                            options={equipment} value={cEquipmentId} size="small" label="Equipment" data-help="maint.workOrderCreate.field.equipment"
                            getOptionLabel={e => `${e.code} - ${e.name}`}
                            disableClientFilter onInputChange={searchEquipment}
                            onChange={id => setCEquipmentId(id)}
                        />
                        <SearchableSelect
                            options={technicians} value={cAssignTo} size="small" label="Assign to (optional)" data-help="maint.workOrderCreate.field.assignTo"
                            getOptionId={t => t.userId} getOptionLabel={t => t.name}
                            onChange={id => setCAssignTo(id)}
                        />
                        <TextField
                            size="small" label="Description" value={cDescription} multiline minRows={2} data-help="maint.workOrderCreate.field.description"
                            onChange={e => setCDescription(e.target.value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreating(false)} data-help="maint.workOrderCreate.cancel">Cancel</Button>
                    <Button data-help="maint.workOrders.action.create" variant="contained" disabled={saving || cTitle.trim() === ''} onClick={create}>Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WorkOrderList;
